"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import Image from "next/image";
import styles from "./ErpNextJourney.module.css";
import { erpNextJourneySteps as STEPS } from "../../data/erp-next";
import { useServiceData } from "../../hooks/useServiceData";

export default function ErpNextJourney() {
  const { data: backendPageData } = useServiceData();
  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  // Find dynamic process section
  const dynamicSection = backendPageData?.extraSections?.find(
    (s) => s.type === "processSection"
  )?.refId;

  // Fallback values
  const displayTag = dynamicSection?.tag || "Our Proven ERPNext Journey";
  const displayTitle = dynamicSection?.title || "From discovery to optimization — built for long-term scalability.";
  const displaySubtitle = dynamicSection?.subtitle || "A sleek 6-step roadmap that visualizes how we take ERPNext from vision to value.";
  
  const displaySteps = dynamicSection?.steps?.map((s, idx) => ({
    no: String(idx + 1).padStart(2, '0'),
    stage: s.title,
    focus: s.description,
    icon: s.icon
  })) || STEPS;

  const getImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
    
    if (url.includes("/uploads/")) {
      const cleanPath = url.substring(url.indexOf("/uploads/"));
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("http")) return url;
    return url;
  };

  const LOOPED = useMemo(() => [...displaySteps, ...displaySteps], [displaySteps]);

  useEffect(() => {
    if (!scrollerRef.current) return;
    const root = scrollerRef.current;

    // Animate connector when item visible
    const opts = { root, threshold: 0.6 };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        if (entry.isIntersecting) el.classList.add("visible");
      });
    }, opts);

    const items = root.querySelectorAll(`.${styles.step}`);
    items.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return; // respect user preference
    if (!scrollerRef.current || !trackRef.current) return;

    let rafId;
    const root = scrollerRef.current;
    const track = trackRef.current;
    const speed = 1.6; // px per frame (faster for clearer motion)
    // ensure programmatic scroll isn't smoothed by CSS
    const prevScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    // disable snap while animating for smoother loop
    root.classList.add(styles.animating);

    const step = () => {
      if (!paused) {
        const half = track.scrollWidth / 2;
        if (!half || half <= 0) {
          rafId = requestAnimationFrame(step);
          return;
        }
        // seamless loop by wrapping at half width (because steps duplicated)
        if (root.scrollLeft >= half) {
          root.scrollLeft -= half;
        }
        root.scrollLeft += speed;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);

    const onEnter = () => setPaused(true);
    const onLeave = () => setPaused(false);
    root.addEventListener("mouseenter", onEnter);
    root.addEventListener("mouseleave", onLeave);
    root.addEventListener("touchstart", onEnter, { passive: true });
    root.addEventListener("touchend", onLeave, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      root.style.scrollBehavior = prevScrollBehavior || "";
      root.classList.remove(styles.animating);
      root.removeEventListener("mouseenter", onEnter);
      root.removeEventListener("mouseleave", onLeave);
      root.removeEventListener("touchstart", onEnter);
      root.removeEventListener("touchend", onLeave);
    };
  }, [paused]);

  return (
    <section className={`${styles.section} py-5`}>
      <div className="container">
        <header className={styles.head} data-reveal="fade">
          <div className={styles.eyebrow}>{displayTag}</div>
          <h3 className={styles.title}>{displayTitle}</h3>
          <p className={styles.subtitle}>{displaySubtitle}</p>
        </header>

        <div
          ref={scrollerRef}
          className={styles.scroller}
          data-reveal="slide-right"
        >
          <div ref={trackRef} className={styles.track}>
            {LOOPED.map((s, idx) => {
              const iconUrl = typeof s.icon === 'string' ? s.icon : (s.icon?.url || '');
              const isImage = typeof iconUrl === 'string' && (iconUrl.startsWith('http') || iconUrl.includes('/') || iconUrl.includes('.'));
              return (
                <article key={`${s.no}-${idx}`} className={styles.step}>
                  <div className={styles.rowTop}>
                    <span className={styles.badge}>{s.no}</span>
                    <div className={styles.iconWrap} aria-hidden style={{ position: 'relative' }}>
                      {isImage ? (
                        <Image src={getImageUrl(iconUrl)} alt={s.stage} style={{ objectFit: 'contain' }} fill />
                      ) : (
                        <i className={iconUrl}></i>
                      )}
                    </div>
                  </div>
                  <h4 className={styles.stepTitle}>{s.stage}</h4>
                  <p className={styles.stepDesc}>{s.focus}</p>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
